import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    // setTranslateOffset measures the actual rendered distance and also
    // computes the animation duration so speed is pixel-accurate in all browsers.
    this.#setTranslateOffset();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /**
   * @type {number | null}
   */
  #marqueeHeight = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.#marqueeAnimation;

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: animation.playbackRate,
      to: 0,
      onUpdate: (value) => { animation.playbackRate = value; },
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.#marqueeAnimation;

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? animation.playbackRate;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => { animation.playbackRate = value; },
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /** @type {Animation | null} */
  #marqueeAnimation = null;

  /**
   * Computes the exact pixel offset for a seamless loop and sets up
   * the native Web Animations API (WAAPI) animation. This guarantees
   * GPU compositor thread execution in Firefox, avoiding the need
   * for `@property` support.
   */
  #setTranslateOffset() {
    const isVertical = this.hasAttribute('data-vertical');
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const { content, wrapper } = this.refs;
    const clone = this.clonedContent;

    let distance = 0;
    if (isVertical) {
      const contentRect = content.getBoundingClientRect();
      const cloneRect = clone?.getBoundingClientRect();
      const gap = cloneRect ? Math.max(0, cloneRect.top - contentRect.bottom) : 0;
      distance = contentRect.height + gap;
    } else {
      const contentRect = content.getBoundingClientRect();
      const cloneRect = clone?.getBoundingClientRect();
      const gap = cloneRect ? Math.max(0, cloneRect.left - contentRect.right) : 0;
      distance = contentRect.width + gap;
    }

    if (this.#marqueeAnimation) {
      this.#marqueeAnimation.cancel();
    }

    if (this.hasAttribute('data-disabled')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let duration = (distance / speedFactor) * 1000;
    if (window.innerWidth <= 749) {
      duration = duration * 0.25;
    }

    const directionAttr = this.getAttribute('data-movement-direction');
    let animationDirection = 'normal';
    if (directionAttr === 'reverse' || directionAttr === 'top-to-bottom') {
      animationDirection = 'reverse';
    }

    // Using a tiny Z-translation forces Firefox to use sub-pixel rendering instead of pixel snapping
    const keyframes = isVertical
      ? [ { transform: 'translate3d(0, 0, 0.01px)' }, { transform: `translate3d(0, ${-distance}px, 0.01px)` } ]
      : [ { transform: 'translate3d(0, 0, 0.01px)' }, { transform: `translate3d(${-distance}px, 0, 0.01px)` } ];

    this.#marqueeAnimation = wrapper.animate(keyframes, {
      duration,
      iterations: Infinity,
      direction: animationDirection,
      easing: 'linear'
    });
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const isVertical = this.hasAttribute('data-vertical');

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true, isVerticalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth, height: marqueeHeight } = firstEntry.rootBounds ?? { width: 0, height: 0 };
          const { width: marqueeItemsWidth, height: marqueeItemsHeight } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          const isVerticalResize = this.#marqueeHeight !== marqueeHeight;

          this.#marqueeWidth = marqueeWidth;
          this.#marqueeHeight = marqueeHeight;

          const dimension = isVertical ? marqueeHeight : marqueeWidth;
          const itemDimension = isVertical ? marqueeItemsHeight : marqueeItemsWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: itemDimension === 0 ? 1 : Math.ceil(dimension / itemDimension),
              isHorizontalResize,
              isVerticalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    // Fix: destructure as 'numberOfCopies' (was incorrectly 'newNumberOfCopies',
    // causing NaN speed on every resize since the key didn't exist on the return value).
    const { numberOfCopies, isHorizontalResize, isVerticalResize } = await this.#queryNumberOfCopies();
    const isVertical = this.hasAttribute('data-vertical');

    // opt out of marquee manipulation on vertical resizes if horizontal, and vice versa
    if (isVertical) {
      if (!isVerticalResize) return;
    } else {
      if (!isHorizontalResize) return;
    }

    const currentNumberOfCopies = marqueeItems.length;

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setTranslateOffset();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    if (this.#marqueeAnimation) {
      this.#marqueeAnimation.currentTime = 0;
    }
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
